import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import background from '#src/assets/header/page_background.webp';
import placeholder from '#src/assets/players/placeholder.webp';
import { FullViewportScroll } from '#src/atoms/container/FullViewportScroll/index.tsx';
import { FullWidthContainer } from '#src/atoms/layout/FullWidthContainer/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandL } from '#src/atoms/typography/index.tsx';
import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { Follower } from '#src/components/player/PlayerPage/Header/Follower/index.tsx';
import type { MetricProps } from '#src/components/seo/HeaderMetrics/index.tsx';
import { HeaderMetrics } from '#src/components/seo/HeaderMetrics/index.tsx';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useIsTabletAndAbove } from '#src/hooks/device/useIsTabletAndAbove.ts';
import { useViewItem } from '#src/hooks/events/useViewItem.ts';
import { laptopAndAbove, mobileAndAbove } from '#src/style/mediaQuery.ts';
import { createVar } from '#src/style/utils.tsx';

import type {
  NewHeader_dataPartner,
  NewHeader_subscription,
} from './__generated__/index.graphql.ts';

const backgroundImgVar = createVar();
const backgroundColorVar = createVar();

const Root = styled.div`
  position: relative;
  isolation: isolate;
  background: no-repeat 50% center / cover url(${background});

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(${backgroundColorVar});
    mix-blend-mode: overlay;
  }
`;

const Picture = styled.div`
  width: 100%;
  height: 420px;
  position: relative;
  @media ${mobileAndAbove} {
    height: 450px;
  }
  &::before {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: var(${backgroundImgVar}) no-repeat;
    background-position: center calc(9 * var(--unit));
    background-size: 400px;
    content: '';

    @media ${laptopAndAbove} {
      background-position: right calc(10 * var(--unit)) top
        calc(var(--unit) * 10);
    }
  }
`;
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, var(--c-black) 0%, transparent 50%);
`;
const TopLine = styled(Vertical)`
  position: absolute;
  top: var(--double-unit);
  left: 0;
  right: 0;
`;

const Top = styled(Vertical)`
  align-items: flex-start;
  @media ${mobileAndAbove} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const Content = styled(Vertical)`
  position: absolute;
  inset: auto 0 0 0;
  z-index: 1;
  justify-content: end;
  padding-bottom: var(--double-unit);

  @media ${laptopAndAbove} {
    padding-bottom: var(--triple-unit);
  }
`;
const StyledHeadlineXL = styled(BrandL)`
  font-size: 48px;
  line-height: 1;

  &.small {
    font-size: 40px;
  }
  &.smaller {
    font-size: 32px;
  }

  @media ${laptopAndAbove} {
    max-width: 800px;
    font-size: 88px;

    &.small {
      font-size: 80px;
    }
    &.smaller {
      font-size: 76px;
    }
  }
`;

const Actions = styled(Horizontal)`
  align-self: flex-end;
`;

type Props = {
  id: string;
  pictureUrl: string | null;
  displayName: string;
  color?: string | null;
  details?: ReactNode;
  scores?: MetricProps[];
  injury?: MetricProps;
  subscription?: NewHeader_subscription;
  dataPartner?: NewHeader_dataPartner | null;
  addToWatchlistNode?: ReactNode;
  metricsRow?: ReactNode;
  breadcrumb?: ReactNode;
};

export const NewHeader = ({
  id,
  pictureUrl,
  displayName,
  color,
  scores = [],
  details,
  injury,
  subscription,
  dataPartner,
  addToWatchlistNode,
  metricsRow,
  breadcrumb,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const isLoggedIn = useIsLoggedIn();
  useViewItem({ id });
  const metrics = [...scores];
  if (injury) metrics.push(injury);

  const showFollower = subscription && isLoggedIn;
  return (
    <Root
      style={{
        [backgroundColorVar]: color,
      }}
    >
      <Picture
        style={{
          [backgroundImgVar]: `url('${proxyUrl(pictureUrl || placeholder, {
            cropWidth: 480,
          })}')`,
        }}
      />
      <Overlay />
      <TopLine>
        <FullWidthContainer>
          <Top>
            {breadcrumb}
            {!!(addToWatchlistNode || showFollower) && (
              <Actions>
                {!!showFollower && (
                  <Follower
                    subscription={subscription}
                    dataPartner={dataPartner}
                    hideCount
                  />
                )}
                {addToWatchlistNode}
              </Actions>
            )}
          </Top>
        </FullWidthContainer>
      </TopLine>

      <Content>
        <FullWidthContainer>
          <Vertical gap={isTabletAndAbove ? 3 : 2}>
            <Vertical gap={0.5}>
              <StyledHeadlineXL
                as="h1"
                className={classNames({
                  small: displayName.length >= 20,
                  smaller: displayName.length >= 40,
                })}
              >
                {displayName}
              </StyledHeadlineXL>
              {details}
            </Vertical>
            {metricsRow || (
              <FullViewportScroll>
                <HeaderMetrics metrics={metrics} />
              </FullViewportScroll>
            )}
          </Vertical>
        </FullWidthContainer>
      </Content>
    </Root>
  );
};

NewHeader.fragments = {
  subscription: gql`
    fragment NewHeader_subscription on WithSubscriptionsInterface {
      slug
      ...Follower_subscription
    }
    ${Follower.fragments.subscription}
  ` as TypedDocumentNode<NewHeader_subscription>,
  dataPartner: gql`
    fragment NewHeader_dataPartner on DataPartner {
      slug
      ...Follower_dataPartner
    }
    ${Follower.fragments.dataPartner}
  ` as TypedDocumentNode<NewHeader_dataPartner>,
};
